import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Play, Volume2, VolumeX, ListOrdered, Lightbulb, Music, Check } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import { useState, useEffect } from 'react';
import * as Tone from 'tone';
import { BREATHING_TECHNIQUES, formatDuration, type BreathingTechniqueKey } from '@/lib/breathing';
import { BreathingOrb } from '@/components/BreathingOrb';
import { loadSoundMode, saveSoundMode, SOUND_MODE_LABEL, type BreathSoundMode } from '@/lib/breathSound';
import { breathMusic, loadBreathTrack, loadMusicEnabled, saveMusicEnabled } from '@/lib/breathMusic';

const NEXT_MODE: Record<BreathSoundMode, BreathSoundMode> = { bell: 'off', off: 'bell' };

/**
 * Экран-интро перед началом практики
 * - Описание техники
 * - 4 шага "как делать"
 * - Когда применять
 * - Статистика
 * - Кнопка "Начать"
 */
export default function BreathingIntroScreen() {
  const nav = useNavigate();
  const { technique: techniqueKey } = useParams<{ technique: BreathingTechniqueKey }>();
  const { haptic } = useTelegram();
  const [soundMode, setSoundMode] = useState<BreathSoundMode>(loadSoundMode);
  // Музыка под дыхание: включена по умолчанию, отключается видимым чеком (решение Славы 13.10.2026)
  const [musicOn, setMusicOn] = useState(loadMusicEnabled);
  const [musicTitle, setMusicTitle] = useState<string | null>(null);
  useEffect(() => {
    loadBreathTrack().then((t) => setMusicTitle(t?.title ?? null));
  }, []);
  // Сфера на интро просто спокойно дышит: четыре секунды вдох, четыре выдох
  const [beat, setBeat] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setBeat((b) => b + 1), 4000);
    return () => window.clearInterval(t);
  }, []);

  const technique = techniqueKey ? BREATHING_TECHNIQUES[techniqueKey] : null;

  if (!technique) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ink-muted">
        Техника не найдена
      </div>
    );
  }

  function toggleSound() {
    haptic('light');
    const next = NEXT_MODE[soundMode];
    setSoundMode(next);
    saveSoundMode(next);
  }

  function startPractice() {
    haptic('medium');
    // Браузер разрешает звук только из обработчика нажатия. Практика стартует сама,
    // без касаний, поэтому будим звук здесь — иначе первый цикл прошёл бы в тишине.
    if (soundMode !== 'off') Tone.start().catch(() => {});
    if (musicOn) breathMusic.prime();
    nav(`/breathing/${techniqueKey}/run`);
  }

  const totalDuration = technique.cycleDuration * technique.recommendedCycles;

  // Считаем количество RCT/исследований для статистики (для box известно 26, для других — 0)
  const evidenceCount = technique.key === 'box' ? '26' : 
                        technique.key === '4_7_8' ? '15' :
                        technique.key === 'coherent' ? '1' :
                        '1';
  const evidenceLabel = technique.key === 'box' ? 'RCT доказано' :
                        technique.key === '4_7_8' ? 'RCT доказано' :
                        technique.key === 'coherent' ? 'Rutgers Univ.' :
                        'Стэнфорд';

  return (
    <div 
      className="min-h-screen flex flex-col max-w-md mx-auto"
      style={{
        background: `linear-gradient(160deg, ${technique.gradientFrom} 0%, ${technique.gradientTo} 100%)`,
      }}
    >
      {/* Header */}
      <div 
        className="px-5 py-4 flex items-center gap-3 border-b"
        style={{ borderColor: `${technique.color}1a` }}
      >
        <button
          onClick={() => nav('/chat?tab=relax')}
          className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors"
          aria-label="Назад"
        >
          <ChevronLeft size={18} className="text-white" />
        </button>
        <div className="flex-1">
          <div 
            className="text-[10px] uppercase tracking-[1.5px] font-medium mb-0.5"
            style={{ color: `${technique.color}cc` }}
          >
            {technique.name} · {technique.rhythmShort}
          </div>
          <div className="text-base font-display font-medium text-white">
            {technique.purpose}
          </div>
        </div>
        <button
          onClick={toggleSound}
          className="h-8 px-2.5 rounded-full flex items-center gap-1.5 text-[11px] font-medium transition-colors"
          style={{
            background: soundMode !== 'off' ? `${technique.color}26` : 'rgba(255,255,255,0.06)',
            color: soundMode !== 'off' ? technique.color : 'rgba(255,255,255,0.5)',
          }}
          aria-label={`Звук: ${SOUND_MODE_LABEL[soundMode]}. Нажми, чтобы сменить`}
        >
          {soundMode !== 'off' ? <Volume2 size={14} /> : <VolumeX size={14} />}
          {SOUND_MODE_LABEL[soundMode]}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 px-5 pt-6 pb-4 overflow-y-auto">
        {/* Дышащий круг */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-6"
        >
          <div className="flex flex-col items-center">
            <BreathingOrb
              size={180}
              color={technique.color}
              phase={beat % 2 === 0 ? 'inhale' : 'exhale'}
              duration={4}
              phaseKey={beat}
            />
            {/* Ритм — под сферой: поверх ярких частиц цифры не читались */}
            <div
              className="-mt-1 text-lg font-display font-medium tabular-nums tracking-[2px]"
              style={{ color: technique.color }}
            >
              {technique.rhythmShort}
            </div>
          </div>
        </motion.div>

        {/* Главная цель */}
        <div className="text-center mb-6">
          <p className="text-sm text-white/65 leading-relaxed">
            {technique.science.split('.')[0] + '.'}
          </p>
        </div>

        {/* Как делать */}
        <div className="mb-5">
          <div 
            className="text-[10px] uppercase tracking-[1.2px] font-medium mb-2 flex items-center gap-1"
            style={{ color: `${technique.color}b3` }}
          >
            <ListOrdered size={11} /> Как делать
          </div>
          <div className="space-y-2">
            {technique.phases.map((phase, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + idx * 0.05 }}
                className="flex items-center gap-2.5 p-3 rounded-xl border"
                style={{
                  background: `${technique.color}0f`,
                  borderColor: `${technique.color}20`,
                }}
              >
                <div 
                  className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px] font-medium tabular-nums shrink-0"
                  style={{
                    background: `${technique.color}33`,
                    color: technique.color,
                  }}
                >
                  {idx + 1}
                </div>
                <div className="text-[13px] text-white flex-1 leading-tight">
                  {phaseInstruction(phase.type, phase.label)}
                </div>
                <div 
                  className="text-[11px] font-medium tabular-nums shrink-0"
                  style={{ color: `${technique.color}b3` }}
                >
                  {phase.duration}с
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Когда применять */}
        <div className="rounded-xl p-3 mb-5 bg-white/[0.04]">
          <div className="text-[10px] uppercase tracking-[1.2px] text-white/50 mb-1 font-medium flex items-center gap-1">
            <Lightbulb size={11} /> Когда применять
          </div>
          <div className="text-[12.5px] text-white/85 leading-snug">
            {technique.whenToUse}
          </div>
        </div>

        {/* Статистика */}
        <div className="flex justify-between px-1 mb-6">
          <div className="text-center">
            <div className="text-lg font-display font-medium text-white tabular-nums">
              {technique.recommendedCycles}
            </div>
            <div className="text-[10px] text-white/50 mt-0.5">циклов</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-display font-medium text-white tabular-nums">
              {formatDuration(totalDuration)}
            </div>
            <div className="text-[10px] text-white/50 mt-0.5">длительность</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-display font-medium text-white tabular-nums">
              {evidenceCount}
            </div>
            <div className="text-[10px] text-white/50 mt-0.5">{evidenceLabel}</div>
          </div>
        </div>
      </div>

      {/* Sticky кнопка Начать */}
      <div className="px-5 pb-24 pt-2">
        {musicTitle && (
          <button
            type="button"
            role="switch"
            aria-checked={musicOn}
            onClick={() => { haptic('light'); setMusicOn((v) => { saveMusicEnabled(!v); return !v; }); }}
            className="w-full mb-2.5 px-3.5 py-3 rounded-2xl bg-white/[0.05] border border-white/[0.08] flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
          >
            <span
              className="w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors"
              style={musicOn
                ? { background: technique.color, borderColor: technique.color, color: technique.gradientFrom }
                : { borderColor: 'rgba(255,255,255,0.3)' }}
            >
              {musicOn && <Check size={13} strokeWidth={3} />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm text-white">Музыка под дыхание</span>
              <span className="block text-[11px] text-white/45 truncate">
                {musicOn ? `${musicTitle} · включится сама и затихнет в конце` : 'Выключена — практика пройдёт в тишине'}
              </span>
            </span>
            <Music size={16} style={{ color: musicOn ? technique.color : 'rgba(255,255,255,0.35)' }} className="shrink-0" />
          </button>
        )}
        <button
          onClick={startPractice}
          className="w-full py-4 rounded-2xl font-display font-medium text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all tracking-[0.3px]"
          style={{
            background: technique.color,
            color: technique.gradientFrom,
            boxShadow: `0 0 24px ${technique.colorRgba}`,
          }}
        >
          <Play size={14} className="fill-current" />
          Начать практику
        </button>
      </div>

    </div>
  );
}

/**
 * Преобразует тип фазы в человеко-читаемую инструкцию
 */
function phaseInstruction(type: string, label: string): string {
  switch (type) {
    case 'inhale':
      return label.toLowerCase().includes('нос') 
        ? 'Медленно вдыхай через нос'
        : 'Медленно вдыхай';
    case 'short_inhale':
      return 'Ещё короткий до-вдох носом';
    case 'hold':
      return 'Задержи дыхание';
    case 'exhale':
      return label.toLowerCase().includes('рот')
        ? 'Полностью выдыхай через рот'
        : 'Медленно выдыхай';
    default:
      return label;
  }
}
